import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { isAssignable } from '../utils/roles';
import ServicesEditor from '../components/ServicesEditor';

const STATUS_AR = {
  active: 'نشط', paused: 'متوقف', completed: 'مكتمل', archived: 'مؤرشف'
};
const STATUS_COLOR = {
  active: 'in_progress', paused: 'pending', completed: 'completed', archived: 'cancelled'
};

const emptyForm = {
  title: '', description: '', client_id: '', sponsor_id: '',
  status: 'active', sheikh_ids: [], member_ids: [], services: [],
};

export default function Projects() {
  const { api, user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [teamUsers, setTeamUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sponsorFilter, setSponsorFilter] = useState(searchParams.get('sponsor_id') || '');
  const [sheikhFilter, setSheikhFilter] = useState(searchParams.get('sheikh_id') || '');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const sheikhClients = clients;

  const load = () => {
    const qs = new URLSearchParams();
    if (search) qs.set('search', search);
    if (statusFilter) qs.set('status', statusFilter);
    if (sponsorFilter) qs.set('sponsor_id', sponsorFilter);
    if (sheikhFilter) qs.set('sheikh_id', sheikhFilter);
    setLoading(true);
    api(`/projects?${qs}`).then(setProjects).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(load, [statusFilter, sponsorFilter, sheikhFilter]);

  useEffect(() => {
    api('/clients').then(setClients).catch(() => {});
    api('/sponsors?active=1').then(setSponsors).catch(() => {});
    api('/users').then(u => setTeamUsers((u || []).filter(isAssignable))).catch(() => {});
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ ...emptyForm, sponsor_id: sponsorFilter || '' });
    setShowModal(true);
  };

  const openEdit = async (p) => {
    setEditing(p);
    setForm({
      title: p.title, description: p.description || '',
      client_id: p.client_id || '', sponsor_id: p.sponsor_id || '',
      status: p.status,
      sheikh_ids: (p.sheikhs || []).map(s => s.id),
      member_ids: (p.members || []).map(m => m.id),
      services: [],
    });
    setShowModal(true);
    // الخدمات تأتي فقط مع تفاصيل المشروع، فنجلبها لتعبئة المحرّر.
    try {
      const full = await api(`/projects/${p.id}`);
      setForm(f => ({
        ...f,
        services: (full.services || []).map(s => ({
          name: s.name, unit: s.unit || '', quantity: s.quantity, unit_price: s.unit_price,
        })),
      }));
    } catch { /* لا مانع من فتح النموذج بدون الخدمات */ }
  };

  const toggleSheikh = (id) => {
    setForm(f => ({
      ...f,
      sheikh_ids: f.sheikh_ids.includes(id)
        ? f.sheikh_ids.filter(x => x !== id)
        : [...f.sheikh_ids, id],
    }));
  };

  const toggleMember = (id) => {
    setForm(f => ({
      ...f,
      member_ids: f.member_ids.includes(id)
        ? f.member_ids.filter(x => x !== id)
        : [...f.member_ids, id],
    }));
  };

  const save = async () => {
    try {
      const services = (form.services || [])
        .filter(s => s.name && s.name.trim())
        .map(s => ({
          name: s.name.trim(),
          unit: s.unit?.trim() || null,
          quantity: Number(s.quantity) || 0,
          unit_price: Number(s.unit_price) || 0,
        }));
      const payload = {
        ...form,
        services,
        client_id: form.client_id || null,
        sponsor_id: form.sponsor_id || null,
      };
      if (editing) {
        await api(`/projects/${editing.id}`, { method: 'PUT', body: JSON.stringify(payload) });
        toast.success('تم تحديث المشروع');
      } else {
        await api('/projects', { method: 'POST', body: JSON.stringify(payload) });
        toast.success('تم إنشاء المشروع');
      }
      setShowModal(false);
      load();
    } catch (err) { toast.error(err.message); }
  };

  const remove = async (p) => {
    if (!window.confirm(`حذف المشروع "${p.title}"؟`)) return;
    try {
      await api(`/projects/${p.id}`, { method: 'DELETE' });
      toast.success('تم حذف المشروع');
      load();
    } catch (err) { toast.error(err.message); }
  };

  const canEdit = ['admin', 'supervisor', 'team'].includes(user?.role);
  const canDelete = ['admin', 'supervisor'].includes(user?.role);
  const formValid = form.title.trim() && (form.sponsor_id || form.client_id);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2>المشاريع</h2>
        {canEdit && (
          <button className="btn btn-primary" data-tour="projects-create" onClick={openNew}>+ مشروع جديد</button>
        )}
      </div>

      <div className="filters">
        <input
          className="form-input"
          placeholder="بحث باسم المشروع..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && load()}
        />
        <select className="form-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">كل الحالات</option>
          {Object.entries(STATUS_AR).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select className="form-select" value={sponsorFilter} onChange={e => setSponsorFilter(e.target.value)}>
          <option value="">كل الداعمين</option>
          {sponsors.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select className="form-select" value={sheikhFilter} onChange={e => setSheikhFilter(e.target.value)}>
          <option value="">كل المشايخ</option>
          {sheikhClients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <button className="btn btn-outline btn-sm" onClick={load}>🔍 بحث</button>
      </div>

      {loading ? (
        <div className="skeleton-grid">
          {[...Array(6)].map((_, i) => <div key={i} className="skeleton-card" />)}
        </div>
      ) : projects.length === 0 ? (
        <div className="card"><div className="empty"><div className="empty-icon">📂</div>لا توجد مشاريع</div></div>
      ) : (
        <div data-tour="projects-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
          {projects.map(p => {
            const total = p.total_tasks || 0;
            const done = p.completed_tasks || 0;
            const pct = total > 0 ? Math.round((done / total) * 100) : 0;
            return (
              <div key={p.id} className="card" style={{ padding: 16, cursor: 'pointer' }}
                onClick={() => navigate(`/projects/${p.id}`)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>{p.title}</div>
                  <span className={`badge badge-${STATUS_COLOR[p.status]}`}>{STATUS_AR[p.status]}</span>
                </div>
                {p.description && (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10, lineHeight: 1.5 }}>
                    {p.description.length > 100 ? p.description.slice(0, 100) + '...' : p.description}
                  </div>
                )}
                {p.sponsor && (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
                    🤝 الداعم: {p.sponsor.name}
                  </div>
                )}
                {p.sheikhs && p.sheikhs.length > 0 && (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
                    👳 {p.sheikhs.length} {p.sheikhs.length === 1 ? 'شيخ' : 'مشايخ'}: {p.sheikhs.slice(0, 2).map(s => s.name).join('، ')}
                    {p.sheikhs.length > 2 && ` +${p.sheikhs.length - 2}`}
                  </div>
                )}
                {p.client && (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
                    👤 العميل: {p.client.name}
                  </div>
                )}
                <div style={{ marginTop: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 11 }}>
                    <span style={{ color: 'var(--text-muted)' }}>التقدم</span>
                    <span>{done}/{total} • {pct}%</span>
                  </div>
                  <div style={{ height: 6, background: 'var(--gray-100)', borderRadius: 3 }}>
                    <div style={{
                      width: `${pct}%`, height: '100%',
                      background: pct === 100 ? 'var(--success)' : 'var(--primary)',
                      borderRadius: 3, transition: 'width .3s'
                    }} />
                  </div>
                </div>
                {canEdit && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 12, flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
                    <button className="btn btn-sm btn-outline" onClick={() => openEdit(p)}>تعديل</button>
                    {canDelete && (
                      <button className="btn btn-sm btn-outline" onClick={() => remove(p)} style={{ color: 'var(--danger)' }}>حذف</button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
            <div className="modal-title">{editing ? 'تعديل مشروع' : 'مشروع جديد'}</div>
            <div className="form-group">
              <label className="form-label">العنوان *</label>
              <input className="form-input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">الوصف</label>
              <textarea className="form-textarea" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">الداعم / صاحب المشروع</label>
                <select className="form-select" value={form.sponsor_id} onChange={e => setForm(f => ({ ...f, sponsor_id: e.target.value }))}>
                  <option value="">— بدون —</option>
                  {sponsors.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">العميل (اختياري)</label>
                <select className="form-select" value={form.client_id} onChange={e => setForm(f => ({ ...f, client_id: e.target.value }))}>
                  <option value="">— بدون —</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">الحالة</label>
              <select className="form-select" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                {Object.entries(STATUS_AR).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">المشايخ المستفيدون ({form.sheikh_ids.length})</label>
              {sheikhClients.length === 0 ? (
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  أضف عملاء بنوع "شيخ" من صفحة العملاء أولاً
                </div>
              ) : (
                <div style={{
                  maxHeight: 180, overflow: 'auto', border: '1px solid var(--border)',
                  borderRadius: 6, padding: 8, display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 6,
                }}>
                  {sheikhClients.map(s => (
                    <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
                      <input
                        type="checkbox"
                        checked={form.sheikh_ids.includes(s.id)}
                        onChange={() => toggleSheikh(s.id)}
                      />
                      {s.name}
                    </label>
                  ))}
                </div>
              )}
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                يمكن إضافة الشيخ نفسه في أكثر من مشروع حسب الجهة الداعمة.
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">فريق العمل ({form.member_ids.length})</label>
              {teamUsers.length === 0 ? (
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  لا يوجد أعضاء قابلون للإسناد
                </div>
              ) : (
                <div style={{
                  maxHeight: 180, overflow: 'auto', border: '1px solid var(--border)',
                  borderRadius: 6, padding: 8, display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 6,
                }}>
                  {teamUsers.map(u => (
                    <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
                      <input
                        type="checkbox"
                        checked={form.member_ids.includes(u.id)}
                        onChange={() => toggleMember(u.id)}
                      />
                      {u.name}
                    </label>
                  ))}
                </div>
              )}
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                أعضاء الفريق المسؤولون عن هذا المشروع. يظهر المشروع للمستخدم المقيَّد بالمشاريع فقط إذا كان ضمن فريقه.
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">الخدمات والتسعير</label>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>
                حدّد خدمات هذا المشروع وأسعارها؛ تُصدَر الفاتورة لاحقاً بناءً عليها.
              </div>
              <ServicesEditor
                value={form.services}
                onChange={services => setForm(f => ({ ...f, services }))}
              />
            </div>
            {!form.sponsor_id && !form.client_id && (
              <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 4 }}>
                يجب تحديد داعم أو عميل للمشروع.
              </div>
            )}
            <div className="modal-actions">
              <button className="btn btn-primary" onClick={save} disabled={!formValid}>
                {editing ? 'حفظ' : 'إنشاء'}
              </button>
              <button className="btn btn-outline" onClick={() => setShowModal(false)}>إلغاء</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
